// WHAT: Portfolio Positions Hook
// WHY:  Fetches active portfolio positions from Supabase with loading and error states
// DOMAIN: Investments → Parqet
// DEPENDENCIES: Supabase client service
// RISKS:
//   - NOTE: Only fetches positions with total_quantity > 0 (excludes closed positions)
//   - NOTE: Error handling logs to console (may not be visible to user)
//   - NOTE: Hook refetches on mount only (no auto-refresh)

import { useEffect, useState, useCallback } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";
import { getSupabaseClient } from "@/shared/services";

export interface PortfolioPosition {
  [key: string]: unknown;
  asset_type: string;
  invested_amount: number;
  isin: string;
  name: string;
  security_id: string;
  symbol: null | string;
  total_quantity: number;
  transaction_count: number;
}

/**
 * WHAT:
 *   Custom hook for fetching active portfolio positions from Supabase.
 *
 * WHY:
 *   Provides portfolio position data with loading and error states for UI components.
 *
 * OUTPUT:
 *   - positions: PortfolioPosition[] — Array of active positions
 *   - loading: boolean — Loading state
 *   - error: string | null — Error message if fetch failed
 *   - refetch: () => Promise<void> — Function to manually refetch positions
 *
 * BEHAVIOR:
 *   - Fetches positions on mount
 *   - Filters to only active positions (total_quantity > 0)
 *   - Orders by invested_amount descending (largest first)
 *   - Sets loading state during fetch
 *   - Returns empty array and error message on failure
 *   - Provides refetch function for manual refresh
 *
 * RISKS:
 *   - NOTE: Only active positions are returned (closed positions excluded)
 *   - NOTE: Error logging uses console.error (may not be visible in production)
 *   - NOTE: No automatic refresh mechanism (must call refetch manually)
 *
 * @returns {object} Object containing positions, loading, error, and refetch function
 *
 */
export const usePortfolioPositions = () => {
  const [positions, setPositions] = useState<PortfolioPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  const fetchPositions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const supabase = await getSupabaseClient();
      const { data, error: fetchError } = await supabase
        .from("portfolio_positions")
        .select("*")
        .gt("total_quantity", 0) // Nur aktive Positionen (shares > 0)
        .order("invested_amount", { ascending: false });

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      setPositions(data ?? []);
    } catch (error_) {
      const errorMessage =
        error_ instanceof Error ? error_.message : "Failed to fetch portfolio positions";
      setError(errorMessage);
      debugLog("error", "investments", "Error fetching portfolio positions", {
        error: errorMessage,
        file: "usePortfolioPositions.ts",
        function: "fetchPositions",
        module: "parqet",
      });
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPositions();
  }, [fetchPositions]);

  return {
    error,
    loading,
    positions,
    refetch: fetchPositions,
  };
};
